//#region libraries
import React, { useEffect, useState } from 'react';
import Modal from "react-modal";
// bootstrap
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from "react-bootstrap/Button";

// custom libraries
import { Card, CustomSpinner } from '_atoms';

// tremor 
import { ColGrid, Title, Subtitle, ToggleItem, Toggle, Col as TremorCol, BarChart, Flex, DateRangePicker } from '@tremor/react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectUserDb } from '_redux/slices/main-screen/user';
import {
    selectDeparturesArrivalsCountByHourForecast,                // list of each hour and the departure + arrival forecasted count for that hour
    selectDailyAnalyticsIsLoading,                              // status flag for forecasting data
    selectDeparturesArrivalsCountByQuarterHourForecast,
    getArrivalsForecastRange,
    getDeparturesForecastRange,
    selectAnalyticsTypeSelector,
    selectDeparturesArrivalsRangeCountByQuarterHourForecast,
    selectDeparturesArrivalsRangeCountByHourForecast,
    resetForecastBookingsRange,
    selectDeparturesForecastRangeStatus,
    selectArrivalsForecastRangeStatus
} from '_redux/slices/prm-bookings/bookings-count';
import { selectAirlineSelectedAirport } from "_redux/slices/prm-bookings/bookings";
import { selectOrder, selectDateToISO, selectSeasonalVarianceFilter } from "_redux/slices/prm-bookings/calendar";
import { selectSeasonalVariances } from "_redux/slices/prm-bookings/seasonal-variance";

// export spreadhseets
import * as XLSX from 'xlsx';

// icons 
import { IoMdClose } from "react-icons/io";

// styles 
import { Colors } from "_styles";

// date 
import moment from 'moment';

//#endregion

const modalStyle = {
    content: {
        top: "30%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "50%",
        width: "30%",
        minHeight: "50%"
    },
};

// data formatter for graph
const dataFormatter = (number) => {
    return Intl.NumberFormat("us").format(number).toString();
};

const ForecastedBookingGraph = (props) => {
    const controller = new AbortController();

    //#region redux
    const dispatch = useDispatch();

    const userDb = useSelector(selectUserDb);
    const isLoading = useSelector(selectDailyAnalyticsIsLoading)
    const airport = useSelector(selectAirlineSelectedAirport);

    // redux data for graph
    const byQuarterData = useSelector(selectDeparturesArrivalsCountByQuarterHourForecast);
    const hourData = useSelector(selectDeparturesArrivalsCountByHourForecast);
    const byQuarterDataRange = useSelector(selectDeparturesArrivalsRangeCountByQuarterHourForecast)
    const hourDataRange = useSelector(selectDeparturesArrivalsRangeCountByHourForecast);
    const departuresForecastRangeStatus = useSelector(selectDeparturesForecastRangeStatus);
    const arrivalsForecastRangeStatus = useSelector(selectArrivalsForecastRangeStatus);
    const order = useSelector(selectOrder);
    const analyticsTypeSelector = useSelector(selectAnalyticsTypeSelector); // 'source' (app/sita/portal) or 'none' (all bookings)

    // seasonal variance
    const seasonalVariances = useSelector(selectSeasonalVariances);
    const seasonalVarianceFilter = useSelector(selectSeasonalVarianceFilter);
    const analyticsDate = useSelector(selectDateToISO); // start date of the week  

    //#endregion

    //#region local states

    const [toggle, setToggle] = useState('Flight Time')
    const [openExportModal, setOpenExportModal] = useState(false)
    const [exportDate, setExportDate] = useState([new Date(), new Date()]);                                   // holds value of the date range for exporting forecast

    const [hourDataLocal, setHourDataLocal] = useState([])


    //#endregion

    //#region useEffect

    // reset listener 
    useEffect(() => {
        dispatch(resetForecastBookingsRange())
    }, [])

    // export Excel file
    useEffect(() => {
        if (arrivalsForecastRangeStatus == "succeeded" && departuresForecastRangeStatus == "succeeded") {
            handleExportData()
            dispatch(resetForecastBookingsRange())
        }
    }, [departuresForecastRangeStatus, arrivalsForecastRangeStatus])

    useEffect(() => {
        let formattedDate = new Date(analyticsDate);
        let hourDataCopy = [...hourData];

        if (seasonalVariances && seasonalVariances.seasonalAdjustments && seasonalVarianceFilter) {

            seasonalVariances.seasonalAdjustments.forEach(seasonalAdjustment => {

                let startDate = new Date(seasonalAdjustment.startDate);
                let endDate = new Date(seasonalAdjustment.endDate);
                let multiplier = seasonalAdjustment.multiplier;

                if (formattedDate >= startDate && formattedDate <= endDate) {

                    hourDataCopy.forEach(hour => {
                        let arrivals = hour.Arrivals + (hour.Arrivals * multiplier);
                        let departures = hour.Departures + (hour.Departures * multiplier);
                        hour.Arrivals = Math.round(arrivals);
                        hour.Departures = Math.round(departures);
                    });

                    // set the data
                    setHourDataLocal(hourDataCopy)

                }

            });
        }
        else {
            // use normal data
            setHourDataLocal(hourData)
        }

        //console.log('seasonalVariances', seasonalVariances)


    }, [hourData, seasonalVariances, analyticsDate, seasonalVarianceFilter])

    useEffect(() => {
        // console.log('hour data local', hourDataLocal);
    }, [hourDataLocal])

    //#endregion

    //#region fucntions

    // close export excel modal 
    const closeExportModal = () => {
        setOpenExportModal(false)
        dispatch(resetForecastBookingsRange())
    }

    // get the forecast by the date range
    const getForecastWithRange = () => {
        const iata = userDb.details.authority;
        let payload = {
            iata: userDb.details.type != "AIRLINE" ? iata : airport,
            start: exportDate[0],
            end: exportDate[1] ? exportDate[1] : exportDate[0],
            limit: 1000000,
            skip: 0,
            search: "",
            order: order,
            carrierCode: userDb.details.type == "AIRLINE" ? iata : "",
            source: analyticsTypeSelector,
            forecast: true,
        }

        // get bookings
        dispatch(getDeparturesForecastRange(payload, controller.signal));

        // get arrivals
        dispatch(getArrivalsForecastRange(payload, controller.signal));
    }

    const handleExportData = () => {
        // Get the correct data based on the toggle
        let data = toggle == 'Flight Time' ? hourDataRange : byQuarterDataRange;

        const startDate = moment(new Date(exportDate[0])).format().split("T")[0]
        const endDate = moment(new Date(exportDate[1] ? exportDate[1] : exportDate[0])).format().split("T")[0]

        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Define the headers for the time slots
        const headers = ['Time', 'Departures', 'Arrivals', 'Total'];

        // Prepare the sheet data
        let sheetData = [];

        // Iterate through each date in the data
        Object.keys(data).forEach(date => {
            // Add the date as a header row
            sheetData.push([date]);

            // Add the regular headers
            sheetData.push(headers);

            // Convert the data array to a suitable format for Excel
            const formattedData = data[date].map(item => ({
                'Time': item.name,
                'Departures': item.Departures,
                'Arrivals': item.Arrivals,
                'Total': item.Departures + item.Arrivals // Calculate the total for each row
            }));

            // Add the formatted data to the sheet data
            formattedData.forEach(row => {
                sheetData.push(Object.values(row));
            });

            // Add an empty row between dates for clarity
            sheetData.push([]);
        });

        // Create the worksheet from the sheet data
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        // Append the worksheet to the workbook with a generic name
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Forecast Report');

        // Generate buffer
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        // Convert buffer to blob
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });

        // Create a URL for the blob object
        const url = window.URL.createObjectURL(blob);

        let fileName = '';
        // create file title
        if (toggle == 'Flight Time') {
            fileName = `${startDate}---${endDate}-forecasted-booking-report-hourly.xlsx`;
        } else {
            fileName = `${startDate}---${endDate}-forecasted-booking-report-quarterly.xlsx`;
        }

        // Create a temporary anchor element and trigger the download
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        // Clean up the URL and remove the temporary anchor element
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    //#endregion

    return (
        <>
            <ColGrid numColsMd={1} numColsLg={6} gapX="gap-x-6" gapY="gap-y-6" marginTop="mt-6">
                <TremorCol numColSpanLg={6}>
                    <Card>

                        {/* title + subtitle */}
                        <Col style={{ marginLeft: 12 }}>
                            <Row>
                                <Flex>
                                    <Col>
                                        <Title>Forecasted Booking Graph</Title>
                                        <Subtitle>View the number of departures and arrivals forecasted per hour</Subtitle>
                                    </Col>
                                    {userDb.details.type != 'AIRLINE' &&
                                        <Toggle color="zinc" defaultValue={toggle} onValueChange={(value) => setToggle(value)}>
                                            <ToggleItem value={'Flight Time'} text="Hourly" />
                                            <ToggleItem value={'Quarter Time'} text="Quarterly" />
                                        </Toggle>
                                    }
                                </Flex>
                            </Row>
                        </Col>

                        {/* forecast graph */}
                        <Row style={{ opacity: isLoading == 'loading' ? 0.2 : 1.0 }}>
                            <BarChart
                                data={toggle == 'Flight Time' ? hourDataLocal : byQuarterData}
                                dataKey="name"
                                categories={["Departures", "Arrivals"]}
                                colors={['blue', 'green']}
                                marginTop="mt-5"
                                yAxisWidth="w-12"
                                valueFormatter={dataFormatter}
                            />
                        </Row>

                        <Row>
                            <Flex>
                                <Button
                                    variant={"outline-secondary"}
                                    // onClick={handleExportData}
                                    onClick={() => setOpenExportModal(true)}
                                    loading={true}
                                    style={{ width: 180, marginLeft: 15, marginTop: 12 }}
                                >
                                    Export
                                </Button>
                            </Flex>
                        </Row>

                    </Card>
                </TremorCol>
            </ColGrid>
            <Modal isOpen={openExportModal} onRequestClose={closeExportModal} style={modalStyle}>
                <div>
                    <Flex>
                        <h4>Export Forecast by date range</h4>
                        <IoMdClose onClick={closeExportModal} style={{ cursor: "pointer" }} color={"grey"} size={32} />
                    </Flex>

                    <hr color={Colors.PRM_LIGHT_BLUE} />

                    <DateRangePicker
                        onValueChange={(value) => setExportDate(value)}
                        value={exportDate}
                        enableDropdown={true}
                        maxWidth="max-w-lg"
                        dropdownPlaceholder={"Today"}
                        minDate={new Date().setMonth(new Date().getMonth() - 12)} // set the minimum selectable date to 12 months ago
                    />
                    <Button
                        variant={"outline-secondary"}
                        onClick={getForecastWithRange}
                        loading={true}
                        style={{ width: 180, marginLeft: 15, marginTop: 12 }}
                    >
                        {departuresForecastRangeStatus == "loading" || arrivalsForecastRangeStatus == "loading" ? <CustomSpinner /> : "Export"}
                    </Button>
                </div>
            </Modal>
        </>

    );
};

export default ForecastedBookingGraph;
